// ---------------------------------------------------------------------------
// U2 library structure cards copy (G3 — library-first creation journey)
//
// Creator-facing copy for the judgment moment where admitted library
// structure candidates are offered as starting points. Same
// Record<LocaleCode, …> pattern as generation-orphan-copy.ts.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { LibraryStructureSelectionKind } from "@/features/project/lib/library-structure-selection";

export interface LibraryStructureCardsCopy {
  /** Judgment-moment headline. */
  heading: string;
  /** One-line explanation: pick a start point, or start fresh. */
  detail: string;
  /** Badge label per admitted asset kind. */
  kindLabels: Record<LibraryStructureSelectionKind, string>;
  /** Honest admission badge — these cards passed promoted+admitted only. */
  admittedBadge: string;
  /** "빈 상태로 시작" card — proceeds exactly as today (G2 default DS). */
  startFresh: string;
  startFreshDetail: string;
  /** G4 stub card — visible but disabled until fork-from-existing lands. */
  importExisting: string;
  comingSoon: string;
}

const COPY: Record<LocaleCode, LibraryStructureCardsCopy> = {
  en: {
    heading: "Start from the library?",
    detail:
      "These admitted library structures match your request. Pick one as the starting point, or start fresh.",
    kindLabels: {
      "actionflow-recipe": "ActionFlow recipe",
      "contract-scenario": "Contract scenario",
    },
    admittedBadge: "Admitted · Promoted",
    startFresh: "Start fresh",
    startFreshDetail: "Generate from your prompt with the default design system.",
    importExisting: "Start from an existing project",
    comingSoon: "Coming soon",
  },
  ko: {
    heading: "라이브러리에서 시작할까요?",
    detail:
      "요청과 맞는 승인된 라이브러리 구조입니다. 시작점으로 선택하거나, 빈 상태로 시작할 수 있어요.",
    kindLabels: {
      "actionflow-recipe": "ActionFlow 레시피",
      "contract-scenario": "계약 시나리오",
    },
    admittedBadge: "승인됨 · 프로모트",
    startFresh: "빈 상태로 시작",
    startFreshDetail: "기본 디자인 시스템으로 프롬프트에서 바로 생성합니다.",
    importExisting: "기존 프로젝트에서 가져오기",
    comingSoon: "곧 제공",
  },
  ja: {
    heading: "ライブラリから始めますか?",
    detail:
      "リクエストに合う承認済みのライブラリ構造です。開始点として選ぶか、白紙から始められます。",
    kindLabels: {
      "actionflow-recipe": "ActionFlow レシピ",
      "contract-scenario": "契約シナリオ",
    },
    admittedBadge: "承認済み · プロモート",
    startFresh: "白紙から始める",
    startFreshDetail:
      "デフォルトのデザインシステムでプロンプトから生成します。",
    importExisting: "既存プロジェクトから始める",
    comingSoon: "近日提供",
  },
  zh: {
    heading: "从库中开始?",
    detail: "以下是与请求匹配的已准入库结构。选择一个作为起点，或从空白开始。",
    kindLabels: {
      "actionflow-recipe": "ActionFlow 配方",
      "contract-scenario": "契约场景",
    },
    admittedBadge: "已准入 · 已晋升",
    startFresh: "从空白开始",
    startFreshDetail: "使用默认设计系统直接从提示生成。",
    importExisting: "从现有项目开始",
    comingSoon: "即将推出",
  },
  "zh-TW": {
    heading: "從庫中開始?",
    detail: "以下是與請求相符的已核准庫結構。選擇一個作為起點，或從空白開始。",
    kindLabels: {
      "actionflow-recipe": "ActionFlow 配方",
      "contract-scenario": "契約情境",
    },
    admittedBadge: "已核准 · 已晉升",
    startFresh: "從空白開始",
    startFreshDetail: "使用預設設計系統直接從提示生成。",
    importExisting: "從現有專案開始",
    comingSoon: "即將推出",
  },
};

export function getLibraryStructureCardsCopy(
  locale: LocaleCode,
): LibraryStructureCardsCopy {
  return COPY[locale] ?? COPY.en;
}
